import { ApplicationConfig, ErrorHandler, provideAppInitializer, provideBrowserGlobalErrorListeners } from '@angular/core';
import { provideRouter, withRouterConfig, withPreloading, NoPreloading, TitleStrategy } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';

import { routes } from './app.routes';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { BeaconTitleStrategy } from './core/beacon-title-strategy';
import { StaleChunkErrorHandler } from './core/stale-chunk-error-handler';
import { adoptSessionFromOpenTab } from './core/auth-token.storage';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    // A tab opened fresh (new tab, deep link) has its own empty sessionStorage,
    // so it asks any already-open tab to hand over the session before the router
    // navigates — otherwise authGuard sees no token and bounces a signed-in user
    // to /login. This has to settle first, which it does: initial navigation runs
    // from an APP_BOOTSTRAP_LISTENER, and those fire only once every app
    // initializer has resolved. Note that adding withEnabledBlockingInitialNavigation()
    // below would break that — it makes the first navigation an app initializer
    // too, and Angular awaits initializers together rather than in order.
    provideAppInitializer(() => adoptSessionFromOpenTab()),
    provideRouter(
      routes,
      withRouterConfig({ paramsInheritanceStrategy: 'always' }),
      // Every feature route is lazy and fetched on demand — nothing is preloaded.
      // Background preloading (even idle-time and staggered) put feature chunks in
      // competition with the multi-MB IA rate feed on a cold /rates load, so the
      // page the user was actually on painted last.
      withPreloading(NoPreloading)
    ),
    provideHttpClient(withInterceptors([authInterceptor])),
    provideAnimationsAsync(),
    { provide: TitleStrategy, useClass: BeaconTitleStrategy },
    // Recovers a tab whose build was replaced by a redeploy: its lazy route chunks
    // no longer exist on the server, so the dynamic import fails and the route
    // never loads. See StaleChunkErrorHandler for the server-side fix this backs up.
    { provide: ErrorHandler, useClass: StaleChunkErrorHandler },
  ]
};